import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { usePermissions } from 'hooks/usePermissions'
import { IBrokerCondition } from 'pages/Admin'
import { pageUrls } from 'pages/Admin/AdminTools/Configuration/FloaterTabs/constants'
import { DocumentHistoryModal } from 'pages/LoanSubmission/DocumentHistoryModal'
import { LoanSubmissionCondition, LoanSubmissionDocument } from 'pages/LoanSubmission/types'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAdminConfig, getLoanSubmission, openS3Document, updateLoanSubmission } from 'services'
import { DocumentFile } from 'stories/components'
import { confirm, formatTime, isLoanLevelPage } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { DocumentsSlide } from './documents'
import { DocumentsSlideNameType } from './documents-simplify'
import { IneligibleParties } from './IneligibleParties'
import { LoanDetailsSlide } from './loanDetails'
import { NotesSlide } from './notes'
import { NotifyBorrowerSlide } from './NotifyBorrower'
import { UnderwritingSlide } from './underwriting'

export function FloaterTabs() {
  const [loading, setLoading] = useState(false)
  const [conditions, setConditions] = useState<LoanSubmissionCondition[]>([])
  const [brokerConditions, setBrokerConditions] = useState<IBrokerCondition[]>([])
  const [currentTemplate, setCurrentTemplate] = useState(-1)
  const [documents, setDocuments] = useState<LoanSubmissionDocument[]>([])
  const [historyDocument, setHistoryDocument] = useState<Record<string, any> | null>(null)
  const [isLoanPage, setIsLoanPage] = useState(false)
  const [floaterTabConfig, setFloaterTabConfig] = useState<Record<string, any>>({})
  const [loanNo, setLoanNo] = useState<string>('0')
  const [hasLoadedSubmission, setHasLoadedSubmission] = useState(false)
  const [isSubmissionLoading, setIsSubmissionLoading] = useState(false)

  const [auth, loanDetail] = useSelector((state: any) => [state.auth, state.loanDetail])

  const { hasPermission } = usePermissions()
  const canDeleteDocumentPermanently = hasPermission('CAN_DELETE_SUBMISSION_DOCUMENTS')
  const brokerProfile = !hasPermission('ADMIN_TO_AE_PROFILE_PERMISSION')
  const canSeeIneligiblePartytab = hasPermission('INELIGIBLE_PARTY_FLOATER')

  useEffect(() => {
    if (!auth.token) return
    getAdminConfig('floaterTabs').then((res) => {
      setFloaterTabConfig(res)
    })
  }, [auth.token])

  useEffect(() => {
    setIsLoanPage(isLoanLevelPage(location.pathname))
    if (!isLoanLevelPage(location.pathname)) return

    const loanNumber = setLoanNumber()
    setLoanNo(loanNumber)
  }, [location.pathname])

  useEffect(() => {
    if (!isLoanPage || !Number(loanNo)) return
    setHasLoadedSubmission(false)
    setConditions([])
    setCurrentTemplate(-1)
    setDocuments([])
    setBrokerConditions([])
  }, [loanNo, isLoanPage])

  const ensureLoanSubmissionLoaded = async () => {
    if (hasLoadedSubmission || isSubmissionLoading || !isLoanPage || !Number(loanNo)) return
    setIsSubmissionLoading(true)
    try {
      setLoading(true)
      const { templateNumber, conditions, brokerConditions, documents } = await getLoanSubmission()
      setConditions(conditions)
      setCurrentTemplate(templateNumber)
      setDocuments(documents)
      setLoading(false)
      if (templateNumber == 0) setBrokerConditions(brokerConditions)
      setHasLoadedSubmission(true)
    } finally {
      setIsSubmissionLoading(false)
    }
  }

  const onAddDocuments = async (docs: DocumentFile[]) => {
    const newDocs = cloneDeep(documents)
    const addingDocs: LoanSubmissionDocument[] = docs.map((doc) => ({
      conditionNo: 0,
      name: doc.name,
      key: doc.fileKey,
      status: doc.status,
      category: doc.category,
      createdAt: doc.createdAt,
      generated: false,
      createdBy: auth.profile.email,
    }))
    newDocs.push(...addingDocs)
    setDocuments(newDocs)
    setLoading(true)
    await updateLoanSubmission({}, { update: addingDocs })
    setLoading(false)
  }

  const onUpdateDocuments = async (newDocs: Array<any>, updatedDoc: any) => {
    setDocuments(newDocs)
    setLoading(true)
    await updateLoanSubmission({}, { update: [updatedDoc] })
    setLoading(false)
  }

  const onRemoveDocument = async (document: any, isRemoveCompletely: boolean = false) => {
    const content = (
      <div className="text-gray-900 mb-4 text-md">
        {isRemoveCompletely
          ? 'Are you sure want to delete this document permanently?'
          : 'Are you sure want to delete this document?'}
        <div className="text-gray-800 flex flex-col text-left text-sm">
          {document.conditionNo ? <span>Condition No: {document.conditionNo}</span> : null}
          <span>Document Name: {document.name}</span>
          <span>Uploaded Date: {formatTime(new Date(document.createdAt))}</span>
        </div>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    const newData = cloneDeep(documents)
    const index = newData.findIndex((doc) => doc.conditionNo == document.conditionNo && doc.key == document.key)
    if (index == -1) return
    if (isRemoveCompletely) {
      if (document.generated) return
      const removedDocs = newData.splice(index, 1)
      setDocuments(newData)
      setLoading(true)
      await updateLoanSubmission({}, { delete: removedDocs })
      setLoading(false)
    } else {
      newData[index].conditionNo = 0
      setDocuments(newData)
      setLoading(true)
      await updateLoanSubmission({}, { update: [newData[index]] })
      setLoading(false)
    }
  }

  const onRemoveDocuments = async (docKeys: string[]) => {
    const newData = cloneDeep(documents)
    const indexes = docKeys
      .map((key) => newData.findIndex((doc) => doc.key == key))
      .filter((v) => v != -1)
      .sort((a, b) => a - b)
    const removedDocs: Record<string, any>[] = []
    indexes.reverse().forEach((index) => {
      if (newData[index].generated) return
      const items = newData.splice(index, 1)
      removedDocs.push(...items)
    })
    setLoading(true)
    await updateLoanSubmission({}, { delete: removedDocs })

    setDocuments(newData)
    setLoading(false)
  }

  const onOpenDocument = async (document: any) => {
    openS3Document(document.key, true)
  }

  const isVisible = (tab: string, props: string = 'visible') => {
    let rtl = false
    Object.keys(pageUrls).map((key) => {
      if (location.pathname.startsWith(pageUrls[key].url)) {
        if (
          props === 'visible' &&
          floaterTabConfig[tab]?.[key]?.visible &&
          floaterTabConfig[tab]?.accountTypes?.[auth.profile.accountType]
        )
          rtl = true
        if (props === 'editable' && floaterTabConfig[tab]?.[key]?.editableAccTypes?.[auth.profile.accountType])
          rtl = true
      }
    })
    return rtl
  }

  if (!isLoanPage || !Number(loanNo)) return <></>

  return (
    <>
      <LayoutLoading show={loading || isSubmissionLoading} />
      {isVisible('loanDetail') && <LoanDetailsSlide />}
      {isVisible('notification') && (
        <NotifyBorrowerSlide
          conditions={conditions}
          brokerProfile={brokerProfile}
          brokerConditions={brokerConditions}
          templateNumber={currentTemplate}
          documents={documents}
          onVisibilityChange={(visible: boolean) => {
            if (visible) ensureLoanSubmissionLoaded()
          }}
        />
      )}
      {isVisible('underwriting') && <UnderwritingSlide />}
      {isVisible('documents') && (
        <>
          {isVisible('documents', 'editable') ? (
            <DocumentsSlide
              loading={loading}
              setLoading={setLoading}
              documents={documents}
              onAdd={onAddDocuments}
              onUpdate={onUpdateDocuments}
              onRemove={(document: any) => onRemoveDocument(document, true)}
              onSelectedRemove={(selectedKeys: string[]) => onRemoveDocuments(selectedKeys)}
              onOpen={(document: any) => onOpenDocument(document)}
              onOpenDocHistory={setHistoryDocument}
              brokerProfile={brokerProfile}
              canDeleteDocumentPermanently={canDeleteDocumentPermanently}
              submitToLoanSetup={loanDetail.submitToLoanSetup}
              onVisibilityChange={(visible: boolean) => {
                if (visible) ensureLoanSubmissionLoaded()
              }}
            />
          ) : (
            <DocumentsSlideNameType loadDocs={true} loading={false} position="right" />
          )}
        </>
      )}
      {isVisible('notes') && <NotesSlide />}
      {canSeeIneligiblePartytab && <IneligibleParties />}
      {historyDocument && <DocumentHistoryModal document={historyDocument} onClose={() => setHistoryDocument(null)} />}
    </>
  )
}
